import { useRef } from 'react';
import AutomationExperience from './components/AutomationExperience';
import ServicePreview from './components/ServicePreview';
import SiteHeader from './components/SiteHeader';
import SiteFooter from './components/SiteFooter';
import { AUTOMATION_STEPS, SERVICE_FAQS } from './data/service';
import { OUR_SERVICES } from './data/ourServices';
import useLandingMotion from './hooks/useLandingMotion';
import './styles/LandingPage.css';
import './styles/LandingMotion.css';

function Arrow() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 12h16m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

function FeatureIcon({ kind }) {
  const paths = {
    inventory: 'm12 3 9 5v8l-9 5-9-5V8l9-5Zm0 10v8M3 8l9 5 9-5M7.5 5.5l9 5V16',
    ads: 'm3 12 18-8-5 17-4-7-9-2Zm9 2 9-10',
    signal: 'M3 12h4l3-8 4 16 3-8h4',
  };
  return <svg width="25" height="25" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d={paths[kind]} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

export default function LandingPage({ onNavigate }) {
  const landingRef = useRef(null);
  useLandingMotion(landingRef);

  const scrollToSection = (event) => {
    const id = event.currentTarget.hash.slice(1);
    const section = document.getElementById(id);
    if (!section) return;
    event.preventDefault();
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.history.replaceState(null, '', `/#${id}`);
    section.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  };

  return (
    <div className="marketfit-landing" ref={landingRef}>
      <a className="mf-skip" href="#main-content">본문 바로가기</a>
      <SiteHeader onNavigate={onNavigate} />

      <main id="main-content" tabIndex={-1}>
        <section className="mf-hero" aria-labelledby="hero-title">
          <div className="mf-container mf-hero-grid">
            <div className="mf-hero-copy">
              <div className="mf-hero-badge" data-reveal><span /> AI 기반 커머스 운영 자동화</div>
              <h1 id="hero-title" data-reveal>재고와 광고,<br /><em>하나의 흐름으로.</em></h1>
              <p className="mf-hero-description" data-reveal>팔리는 이유를 이해하고, 다음 수요를 준비하세요.<br />마켓핏이 흩어진 데이터를 연결해<br className="mf-desktop-break" /> 재고와 광고 운영의 다음 액션을 보여드립니다.</p>
              <div className="mf-hero-actions" data-reveal>
                <a className="mf-button" href="#how-it-works" onClick={scrollToSection}>자동화 흐름 체험하기 <Arrow /></a>
                <a className="mf-button mf-button-secondary" href="/services" onClick={onNavigate}>서비스 보러 가기 <Arrow /></a>
              </div>
              <div className="mf-hero-footnote" data-reveal><span className="mf-tiny-check">✓</span> 재고 판단부터 광고 전략까지, 같은 데이터로.</div>
            </div>
            <div className="mf-hero-preview" data-reveal="preview">
              <div className="mf-preview-orbit" aria-hidden="true" />
              <ServicePreview />
              <div className="mf-preview-caption"><span /> CONNECT YOUR DATA. MOVE AS ONE.</div>
            </div>
            <a className="mf-scroll-cue" href="#how-it-works" onClick={scrollToSection}><span>SCROLL TO CONNECT</span><span aria-hidden="true">↓</span></a>
          </div>
        </section>

        <section className="mf-data-strip" aria-label="마켓핏이 연결하는 데이터">
          <div className="mf-container mf-data-strip-inner">
            <p data-reveal>따로 쌓이던 데이터,<br /><strong>함께 움직이는 비즈니스.</strong></p>
            <div className="mf-data-sources" data-reveal><span>상품·판매</span><i>+</i><span>재고·수요</span><i>+</i><span>광고·행동</span><i>+</i><span>리뷰·CS</span><span className="mf-data-result">MarketFit <Arrow /></span></div>
          </div>
        </section>

        <AutomationExperience />

        <section id="solutions" className="mf-solutions mf-section" aria-labelledby="solutions-title">
          <div className="mf-container">
            <div className="mf-section-heading mf-heading-row" data-reveal>
              <div><span className="mf-eyebrow">ONE PLATFORM, BETTER DECISIONS</span><h2 id="solutions-title">재고팀과 마케팅팀이<br />같은 방향으로.</h2></div>
              <p>각자의 보고서를 넘어서,<br />서로 연결된 운영의 기준을 만듭니다.</p>
            </div>
            <div className="mf-solution-grid">
              <article className="mf-solution-card mf-inventory-card" data-reveal>
                <div className="mf-feature-label"><FeatureIcon kind="inventory" /><span>INVENTORY AUTOMATION</span></div>
                <h3>다음 수요에 맞춘<br />재고 운영.</h3>
                <p>판매 이력과 시장 반응으로 수요를 예측하고,<br className="mf-desktop-break" /> 적정 재고와 리밸런싱의 기준을 세웁니다.</p>
                <ul className="mf-check-list"><li>수요 시나리오에 따른 운영 판단</li><li>과잉·부족 재고의 우선순위 확인</li><li>데이터 기반 재고 리밸런싱</li></ul>
                <div className="mf-inventory-visual" aria-label="재고 운영 흐름 예시">
                  <div className="mf-mini-heading"><span>재고 운영 플랜</span><span>흐름 예시</span></div>
                  <div className="mf-stock-row"><span>판매 추이</span><div><i style={{ width: '56%' }} /></div><b>수요 파악</b></div>
                  <div className="mf-stock-row"><span>수요 전망</span><div><i style={{ width: '76%' }} /></div><b>시나리오</b></div>
                  <div className="mf-stock-row"><span>재고 운영</span><div><i style={{ width: '66%' }} /></div><b>우선순위</b></div>
                  <div className="mf-mini-result"><span>↗</span> 수요 변화에 맞춰 운영 계획을 업데이트</div>
                </div>
              </article>
              <article className="mf-solution-card mf-ad-card" data-reveal>
                <div className="mf-feature-label"><FeatureIcon kind="ads" /><span>AD AUTOMATION</span></div>
                <h3>팔리는 이유를 아는<br />광고 운영.</h3>
                <p>성과의 숫자에 고객의 목소리를 더합니다.<br className="mf-desktop-break" /> 상품의 강점과 개선점을 다음 전략으로 연결합니다.</p>
                <ul className="mf-check-list"><li>리뷰·CS 기반 고객 반응 분석</li><li>광고·판매 성과의 영향 요인 파악</li><li>상품별 마케팅 개선 우선순위 제안</li></ul>
                <div className="mf-ad-visual" aria-label="광고 전략 분석 흐름 예시">
                  <div className="mf-mini-heading"><span>고객 반응 → 다음 전략</span><span>흐름 예시</span></div>
                  <div className="mf-review-quote"><span>“</span>고객은 어떤 점에 반응하고 있을까요?</div>
                  <div className="mf-insight-tags"><span>상품 강점</span><span>구매 이유</span><span>개선 요청</span></div>
                  <div className="mf-mini-result"><span>↗</span> 고객 반응을 광고 메시지와 개선 액션으로</div>
                </div>
              </article>
            </div>
            <div className="mf-alert-feature" data-reveal><span className="mf-alert-icon"><FeatureIcon kind="signal" /></span><div><h3>변화는 빠르게 감지하고, 대응은 근거 있게.</h3><p>클릭·전환·판매의 이상 신호를 원인 진단과 알림·리포트로 연결합니다.</p></div></div>
          </div>
        </section>

        <section id="technology" className="mf-technology mf-section" aria-labelledby="technology-title">
          <div className="mf-container mf-technology-grid">
            <div className="mf-section-heading" data-reveal><span className="mf-eyebrow">BUILT TO CONNECT</span><h2 id="technology-title">다섯 가지 기술.<br />하나의 운영 시스템.</h2><p>데이터를 모으는 데서 끝나지 않도록.<br />이해하고, 예측하고, 판단하는 기술을<br className="mf-desktop-break" /> 하나의 흐름으로 설계했습니다.</p></div>
            <div className="mf-technology-list">
              {AUTOMATION_STEPS.map((step) => <article key={step.id} data-reveal><span className="mf-tech-number">{step.number}</span><div><h3>{step.short}</h3><p>{step.output}</p></div></article>)}
            </div>
          </div>
        </section>

        <section id="faq" className="mf-faq mf-section" aria-labelledby="faq-title">
          <div className="mf-container mf-faq-grid">
            <div className="mf-section-heading" data-reveal><span className="mf-eyebrow">A LITTLE MORE ABOUT MARKETFIT</span><h2 id="faq-title">궁금한 점을<br />모았습니다.</h2></div>
            <div className="mf-faq-list">{SERVICE_FAQS.map((faq) => <details key={faq.question} data-reveal><summary>{faq.question}<span aria-hidden="true">+</span></summary><p>{faq.answer}</p></details>)}</div>
          </div>
        </section>

        <section id="our-services" className="mf-our-services mf-section" aria-labelledby="our-services-title">
          <div className="mf-container">
            <div className="mf-section-heading mf-heading-row" data-reveal>
              <div><span className="mf-eyebrow">OUR SERVICES</span><h2 id="our-services-title">우리의 서비스</h2></div>
              <p>광고와 판매부터 물품 생산과 유통까지.<br />비즈니스에 필요한 다음 흐름을 만나보세요.</p>
            </div>
            <div className="mf-service-teasers">
              {OUR_SERVICES.map((service) => (
                <a key={service.id} className={`mf-service-teaser mf-teaser-${service.id}`} href={`/services#${service.id}`} onClick={onNavigate} data-reveal aria-label={`${service.name} 서비스 알아보기`}>
                  <div className="mf-teaser-top"><span>{service.number} / {service.category}</span><Arrow /></div>
                  <h3><span className="mf-teaser-mark" aria-hidden="true">{service.id === 'united-ad' ? <><i /><i /><i /><i /></> : '↗'}</span>{service.name}<span className="mf-teaser-dot">.</span></h3>
                  <p>{service.summary}</p>
                  <div className="mf-teaser-tags">{service.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
                </a>
              ))}
            </div>
            <div className="mf-our-services-action" data-reveal><a className="mf-button mf-button-secondary" href="/services" onClick={onNavigate}>서비스 보러 가기 <Arrow /></a></div>
          </div>
        </section>

        <section className="mf-final-cta" aria-labelledby="cta-title">
          <div className="mf-container" data-reveal><span className="mf-eyebrow">YOUR NEXT MOVE, CONNECTED.</span><h2 id="cta-title">더 나은 다음 액션을,<br />마켓핏과 함께.</h2><p>흩어진 데이터가 하나의 판단이 되는 과정을 직접 경험해 보세요.</p><a href="#how-it-works" onClick={scrollToSection} className="mf-button mf-button-white">마켓핏 흐름 체험하기 <Arrow /></a></div>
          <div className="mf-cta-rings" aria-hidden="true" data-reveal="rings"><i /><i /><i /></div>
        </section>
      </main>

      <SiteFooter onNavigate={onNavigate} />
    </div>
  );
}
